import { UnshadowedElement, html } from "../lib/unshadowed.js";
import { PostJSON } from "../lib/fetchers.js";
import { FormToObject } from "../lib/form-utilities.js";

import { BackButton } from "./back-button.js";
import { SubmitStatus } from "./submit-status.js";

BackButton.Register();
SubmitStatus.Register();

/**
 * Form wrapper with helper functions
 * Creates a standardized form, with a sticky-button-bar on the bottom
 * Handles form submission, button loading state, status message, dirty and validated states
 * Makes any inputs tagged with enterkeyhint="next" intercept the enter key and set focus to next input
 */
export class FormHelper extends UnshadowedElement {
    static elementName() { return "form-helper" }

    static properties = {
        /** URL to post the data to */
        postUrl: { type: String },
        /** Alternately, a function to run on submit. Preempts postUrl */
        submitFunction: { type: Function },
        /** Message to show on success */
        successMsg: { type: String },
        /** Message to show in submit button while posting */
        loadingMsg: { type: String },
        /** Back button can be hidden */
        backButtonHidden: { type: Boolean },
        /** Don't prompt the user if the form is dirty */
        preventDirtyWatch: { type: Boolean },
        /** Optional text to override back button text */
        backButtonText: { type: String },
        /** URL for back button destination */
        backButtonUrl: { type: String },
        /** Submit button text */
        submitButtonText: { type: String },
        /** Optional class for the form content and sticky buttons. "center" if not provided */
        wrapperClass: { type: String },
        /** Transformer function for formdata. Set this via JS after load. Should take in formdata, and modify it in place */
        transformer: { type: Function },
        /** SuccessProcessor function for successful post results. Set this via JS after load. Should take in post result object for processing */
        successProcessor: { type: Function },
        /** Submit button can be hidden */
        submitButtonHidden: { type: Boolean },

        content: { state: true, type: Array },
        dirty: { state: true, type: Array },
        validated: { state: true, type: Boolean },
    }

    constructor() {
        super();
        // Defaults
        this.wrapperClass = "center";
    }

    connectedCallback() {
        super.connectedCallback();
        // Intercept "enter" to behave like tab, if source input has enterkeyhint="next"
        try {
            /** @type {HTMLInputElement[]} all inputs in the form that should intercept enter */
            const nextSources = this.querySelectorAll("[enterkeyhint=next]");
            for (const nextSource of nextSources) {
                nextSource.addEventListener("keydown", e => {
                    if (e.key === "Enter") {
                        /** @type {HTMLInputElement[]} all inputs in the form that can be tabbed into */
                        const destinations = Array.from(this.querySelectorAll("input:not([type=hidden]):not([disabled]):not([type=button]):not([type=submit]), textarea"));
                        // Find the input in the big array since we don't want to only tab to "enterkeyhint" inputs
                        const index = destinations.indexOf(nextSource);
                        if (index > -1 && index < destinations.length - 1) {
                            destinations[index + 1].focus();
                            e.preventDefault();
                        }
                    }
                });
            }
        } catch (err) {
            console.error("Unable to intercept Enter key");
            console.error(err);
        }

        // Scroll-margin doesn't always work in chromium browsers (for textarea), so we need to scroll focused inputs above the sticky bar manually
        try {
            const focusables = this.querySelectorAll("input, textarea");
            for (const element of focusables) {
                element.addEventListener("focus", () => {
                    const elementBottom = element.getBoundingClientRect().bottom;
                    const viewportHeight = window.innerHeight;
                    const stickyFooter = document.querySelector(".sticky-button-bar");

                    if (stickyFooter && elementBottom + stickyFooter.offsetHeight > viewportHeight) {
                        window.scrollBy(0, 200);
                    }
                });
            }
        } catch (err) {
            console.error("Unable to intercept input focus");
            console.error(err);
        }

        // Slots don't work without shadow DOM, so we have to grab the contents ourselves
        this.content = Array.from(this.childNodes);
    }

    async PerformSubmit() {
        const submitButton = this.querySelector("[data-formhelper-submit-button]");
        SubmitStatus.Hide("form-helper-status");
        // Gather up all form fields as an object
        const form = this.querySelector("form");
        let data = FormToObject(form);
        // If a transformer is provided, let it process the data
        if (this.transformer) {
            this.transformer(data);
        }
        try {
            if (this.loadingMsg && submitButton) {
                // Set loading status on submit button
                submitButton.classList.add("button-loading");
                submitButton.setAttribute("disabled", "disabled");
                submitButton.innerText = this.loadingMsg;
            }
            if (this.submitFunction) {
                await this.submitFunction(data);
                // Return submit button to normal
                if (this.loadingMsg && submitButton) {
                    submitButton.classList.remove("button-loading");
                    submitButton.removeAttribute("disabled");
                    submitButton.innerHTML = this.submitButtonText;
                }
            } else {
                const result = await PostJSON(this.postUrl, data);
                if (result.returnUrl) {
                    location.href = result.returnUrl;
                    // Leave the submit button be and don't show success, since we're redirecting
                } else {
                    SubmitStatus.Set("form-helper-status", result.message || this.successMsg);
                    form.dispatchEvent(new Event("pristine"));
                    if (this.successProcessor) {
                        this.successProcessor(result.obj);
                    }
                    // Return submit button to normal
                    if (this.loadingMsg && submitButton) {
                        submitButton.classList.remove("button-loading");
                        submitButton.removeAttribute("disabled");
                        submitButton.innerHTML = this.submitButtonText;
                    }
                }
            }
        } catch (err) {
            SubmitStatus.Set("form-helper-status", err.message || "The action did not complete. Please try again.", true);
            // Return submit button to normal
            if (this.loadingMsg && submitButton) {
                submitButton.classList.remove("button-loading");
                submitButton.removeAttribute("disabled");
                submitButton.innerHTML = this.submitButtonText;
            }
        }
    }

    render() {
        let buttonContent = [];
        if (!this.backButtonHidden) {
            buttonContent.push(html`<back-button href=${this.backButtonUrl} text=${this.backButtonText} ?preventDirtyWatch=${this.preventDirtyWatch} buttonClass="button:size-responsive button-outline button-rectangle"></back-button>`);
        }
        if (!this.submitButtonHidden) {
            buttonContent.push(html`<button type="submit" @click=${() => this.validated = true} class="button button:size-responsive button-orange button-rectangle submit-button" data-formhelper-submit-button>${this.submitButtonText}</button>`);
        }

        return html`
            <form
                @submit=${this.PerformSubmit}
                @change=${() => this.dirty = true}
                class="space-children:stack:space-child-default ${this.validated ? "was-validated" : null}"
                action="javascript:void(0);"
                autocomplete="off"
            >
                <div class="${this.wrapperClass}">
                    ${this.content}
                </div>
                <div class="sticky-button-bar bottom" style="--this-distance: -1px;">
                    <div class="${this.wrapperClass} button-row">
                        <submit-status id="form-helper-status"></submit-status>
                        ${buttonContent}
                    </div>
                </div>
            </form>
        `;
    }

    observer = null;
    updated() {
        // Create an intersection observer for the sticky bar to toggle the shadow
        if (!this.observer) {
            const el = document.querySelector(".sticky-button-bar")
            if (el) {
                this.observer = new IntersectionObserver(
                    ([e]) => e.target.classList.toggle("threshold-reached", e.intersectionRatio < 1),
                    { threshold: [1] }
                );
                this.observer.observe(el);
            }
        }
    }
}
